import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription, forkJoin } from 'rxjs';

import { LoaderComponent } from 'src/app/components/widgets/loader/loader.component';
import { RegulatorsService } from 'src/app/services/api/regulators.service';
import { AlertNotificationService } from 'src/app/services/alert-notification.service';
import { CommonService } from 'src/app/services/common.service';
import { MainLayoutService } from 'src/app/services/main-layout.service';
import { RouteService } from 'src/app/services/route.service';
import {
  Regulator,
  RegulatorClient,
  ScanCheck,
  ScanDetail,
  regulatorStatusBadgeClass,
  scanCheckBadgeClass,
  scanCheckLabel,
  scanComplianceBadgeClass,
  scanComplianceLabel,
  scanStatusWithStageLabel,
  scanTriggerTypeLabel
} from 'src/app/models/entities/regulator';
import { RegulatorContextBoxComponent, RegulatorContextItem } from '../components/regulator-context-box/regulator-context-box.component';
import { apiErrorMessage, formatJsonPayload, toWebsiteHref } from '../regulators-ui.utils';

@Component({
  selector: 'app-regulator-scan-detail-page',
  imports: [LoaderComponent, RegulatorContextBoxComponent],
  templateUrl: './regulator-scan-detail-page.component.html',
  styleUrl: './regulator-scan-detail-page.component.scss',
})
export class RegulatorScanDetailPageComponent implements OnInit, OnDestroy {

  readonly regulatorId: number;
  readonly clientId: number;
  readonly websiteId: number;
  readonly scanId: number;

  readonly statusLabel = scanStatusWithStageLabel;
  readonly statusBadgeClass = regulatorStatusBadgeClass;
  readonly complianceLabel = scanComplianceLabel;
  readonly complianceBadgeClass = scanComplianceBadgeClass;
  readonly triggerTypeLabel = scanTriggerTypeLabel;
  readonly checkLabel = scanCheckLabel;
  readonly checkBadgeClass = scanCheckBadgeClass;
  readonly toHref = toWebsiteHref;

  isLoading = true;
  scan: ScanDetail | null = null;
  checks: ScanCheck[] = [];
  contextItems: RegulatorContextItem[] = [];
  formattedPayload = '';
  showRawPayload = false;

  screenshotUrl: string | null = null;
  isLoadingScreenshot = false;
  screenshotError = false;
  isDownloadingReport = false;

  private subscriptions = new Subscription();

  constructor(
    private regulatorsService: RegulatorsService,
    private routeService: RouteService,
    private mainLayoutService: MainLayoutService,
    private alertNotificationService: AlertNotificationService,
    private commonService: CommonService,
    private route: ActivatedRoute,
    private router: Router
  ) {
    const params = this.route.snapshot.paramMap;
    this.regulatorId = Number(params.get('regulatorId'));
    this.clientId = Number(params.get('clientId'));
    this.websiteId = Number(params.get('websiteId'));
    this.scanId = Number(params.get('scanId'));
  }

  ngOnInit(): void {
    forkJoin({
      regulator: this.regulatorsService.detail(this.regulatorId),
      client: this.regulatorsService.detailClient(this.regulatorId, this.clientId),
      scan: this.regulatorsService.detailScan(this.regulatorId, this.clientId, this.websiteId, this.scanId),
    }).subscribe({
      next: ({ regulator, client, scan }: any) => {
        this.applyScan(regulator?.data?.regulator, client?.data?.client, scan?.data?.scan);
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
        this.router.navigate(['/error/404']);
      }
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.revokeScreenshotUrl();
    this.mainLayoutService.setBreadcrumbs(null);
  }

  goBack(): void {
    this.routeService.regulatorWebsiteScans(this.regulatorId, this.clientId, this.websiteId);
  }

  formatDateTime(value: string | null | undefined): string {
    return this.commonService.formatDateValue(value, true);
  }

  toggleRawPayload(): void {
    this.showRawPayload = !this.showRawPayload;
  }

  openScreenshotFullSize(): void {
    if (this.screenshotUrl) {
      window.open(this.screenshotUrl, '_blank', 'noopener');
    }
  }

  downloadReport(): void {
    if (this.isDownloadingReport) {
      return;
    }

    this.isDownloadingReport = true;
    this.regulatorsService.scanReport(this.regulatorId, this.clientId, this.websiteId, this.scanId).subscribe({
      next: (blob: Blob) => {
        this.isDownloadingReport = false;
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `scan-${this.scanId}-report.pdf`;
        link.click();
        setTimeout(() => URL.revokeObjectURL(url));
      },
      error: (error: any) => {
        this.isDownloadingReport = false;
        this.alertNotificationService.error(
          error?.status === 404 ? 'Report not available' : apiErrorMessage(error, 'Error downloading the report')
        );
      }
    });
  }

  private applyScan(
    regulator: Regulator | null | undefined,
    client: RegulatorClient | null | undefined,
    scan: ScanDetail | null | undefined
  ): void {
    if (!regulator || !client || !scan) {
      return;
    }

    this.scan = scan;
    this.checks = [...(scan.checks ?? [])]
      .sort((a, b) => (a.position ?? Number.MAX_SAFE_INTEGER) - (b.position ?? Number.MAX_SAFE_INTEGER));
    this.formattedPayload = formatJsonPayload(scan.rawPayload);

    const websiteLabel = scan.url ?? `#${this.websiteId}`;
    this.contextItems = [
      { label: 'Regulator', code: regulator.code, value: regulator.name },
      { label: 'Client', code: client.code, value: client.name },
      { label: 'Website', value: websiteLabel },
    ];
    const websitesLink = `/regulators/${regulator.regulatorId}/clients/${client.clientId}`;
    this.mainLayoutService.setBreadcrumbs([
      { label: 'Regulators', routerLink: '/regulators' },
      { label: regulator.code },
      { label: 'Clients', routerLink: `/regulators/${regulator.regulatorId}/clients` },
      { label: client.name, routerLink: websitesLink },
      { label: 'Scans', routerLink: `${websitesLink}/websites/${this.websiteId}/scans` },
      { label: `#${scan.scanId}` },
    ]);

    if (scan.hasScreenshot) {
      this.loadScreenshot();
    }
  }

  private loadScreenshot(): void {
    this.isLoadingScreenshot = true;
    this.screenshotError = false;
    this.subscriptions.add(
      this.regulatorsService.scanScreenshot(this.regulatorId, this.clientId, this.websiteId, this.scanId).subscribe({
        next: (blob: Blob) => {
          this.isLoadingScreenshot = false;
          this.revokeScreenshotUrl();
          this.screenshotUrl = URL.createObjectURL(blob);
        },
        error: () => {
          this.isLoadingScreenshot = false;
          this.screenshotError = true;
        }
      })
    );
  }

  private revokeScreenshotUrl(): void {
    if (this.screenshotUrl) {
      URL.revokeObjectURL(this.screenshotUrl);
      this.screenshotUrl = null;
    }
  }
}
